import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MapPin } from "lucide-react";

const LINE_EASE = [0.16, 1, 0.3, 1];

const KineticLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: LINE_EASE, delay }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const BARS = [
  34, 62, 45, 80, 52, 95, 40, 70, 88, 30, 64, 100, 48, 76, 55, 90,
  38, 68, 84, 44, 58, 98, 36, 72, 60, 86, 42, 78, 50, 92, 33, 66,
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const vinylY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const vinylRotate = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen grid-bg flex flex-col justify-center overflow-hidden pt-24 pb-16"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(0,240,255,0.06),transparent_55%)]" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full relative z-10 grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-center">
        <motion.div style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mono text-[11px] sm:text-xs tracking-[0.35em] text-[#00F0FF] mb-8 flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <MapPin className="h-3.5 w-3.5" />
            RECORDS OF SAN DIEGO // EST. MMVI
          </motion.p>

          <h1 className="font-display uppercase leading-[0.92] text-[15vw] sm:text-7xl lg:text-8xl xl:text-[7.5rem] tracking-tight" data-testid="hero-headline">
            <KineticLine delay={0.15}>WHERE</KineticLine>
            <KineticLine delay={0.3} className="text-stroke-cyan glitch-layer">CHAOS</KineticLine>
            <KineticLine delay={0.45}>MEETS <span className="text-[#CCFF00]">DATA</span></KineticLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: LINE_EASE }}
            className="mt-8 max-w-xl text-sm sm:text-base text-[#A0A0AB] leading-relaxed"
            data-testid="hero-subcopy"
          >
            Plagiarhythm turns overlooked talent into commercially viable brands —
            hands-on artist development, A&R strategy, Aidar.ai-powered data analysis,
            and relationship-driven marketing. Every artist we touched was uncharted
            before us. Every one of them charted after.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9, ease: LINE_EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group mono text-xs tracking-[0.2em] px-7 py-4 bg-[#00F0FF] text-black font-semibold hover:bg-[#CCFF00] transition-colors duration-300 flex items-center gap-2"
            >
              START A PROJECT
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </a>
            <a
              href="#track-record"
              data-testid="hero-cta-secondary"
              className="mono text-xs tracking-[0.2em] px-7 py-4 border border-[#22222B] text-[#F2F2F5] hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors duration-300"
            >
              SEE THE PROOF
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: vinylY, rotate: vinylRotate }}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 1.2, ease: LINE_EASE }}
          className="relative hidden lg:flex items-center justify-center"
          data-testid="hero-vinyl"
        >
          <div className="vinyl-disc vinyl-spin h-[380px] w-[380px] xl:h-[440px] xl:w-[440px] relative flex items-center justify-center">
            <div className="h-[38%] w-[38%] rounded-full bg-[#CCFF00] flex flex-col items-center justify-center gap-1">
              <span className="mono text-[9px] tracking-[0.3em] text-black font-semibold">PLAGIARHYTHM</span>
              <span className="mono text-[8px] tracking-[0.2em] text-black/70">33⅓ RPM // SIDE A</span>
            </div>
            <div className="absolute h-3 w-3 rounded-full bg-[#08080A] border border-[#22222B]" />
          </div>
          <div className="absolute -bottom-8 -right-4 mono text-[10px] tracking-[0.25em] text-[#60606E] tech-corner px-4 py-3 bg-[#111115]/80 border border-[#22222B]">
            SIG: 44.1kHz // SD → WORLD
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-0 left-0 right-0 h-16 flex items-end gap-[3px] px-5 sm:px-8 opacity-60"
        data-testid="hero-waveform"
      >
        {BARS.map((h, i) => (
          <span
            key={i}
            className="eq-bar flex-1 bg-[#00F0FF]/50"
            style={{ height: `${h * 0.5}%`, animationDelay: `${i * 70}ms` }}
          />
        ))}
      </motion.div>
    </section>
  );
}
